export interface MaeizEnvelope<T = Record<string, unknown>> {
    ok?: boolean;
    status?: number | string;
    invokedMethod?: string;
    invokedEndpoint?: string;
    responseTime?: number;
    timestamp?: string;
    message?: string;
    data?: T;
    meta?: unknown;
    [key: string]: unknown;
}

export interface MaeizListEnvelope<T> extends MaeizEnvelope {
    currentPage?: number;
    lastPage?: number;
    perPage?: number;
    total?: number;
    from?: number | null;
    to?: number | null;
    [key: string]: unknown | T[];
}

export interface MaeizErrorDetail {
    code?: string;
    message?: string;
    details?: {
        validation?: Record<string, string[]>;
        code?: string;
        [key: string]: unknown;
    };
    requestId?: string;
}

export interface MaeizErrorEnvelope extends MaeizEnvelope {
    error?: MaeizErrorDetail;
    code?: string;
}

const RESERVED_ENVELOPE_KEYS = new Set([
    "ok",
    "status",
    "invokedMethod",
    "invokedEndpoint",
    "responseTime",
    "timestamp",
    "message",
    "data",
    "meta",
    "error",
]);

export function isOkEnvelope(value: unknown): value is MaeizEnvelope {
    return Boolean(value) && typeof value === "object" && (value as MaeizEnvelope).ok === true;
}

export function envelopeField<T = unknown>(
    envelope: MaeizEnvelope | undefined | null,
    key: string,
    fallback?: T
): T {
    if (!envelope || typeof envelope !== "object") {
        return fallback as T;
    }
    const direct = (envelope as Record<string, unknown>)[key];
    if (direct !== undefined) {
        return direct as T;
    }
    const nested = envelope.data as Record<string, unknown> | undefined;
    if (nested && nested[key] !== undefined) {
        return nested[key] as T;
    }
    return fallback as T;
}

export function envelopeBody<T = Record<string, unknown>>(
    envelope: MaeizEnvelope<T> | undefined | null
): T | undefined {
    if (!envelope || typeof envelope !== "object") {
        return undefined;
    }
    if (envelope.data !== undefined) {
        return envelope.data;
    }
    const body: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(envelope)) {
        if (!RESERVED_ENVELOPE_KEYS.has(key)) {
            body[key] = val;
        }
    }
    return body as T;
}

export interface MaeizListResult<T> {
    items: T[];
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
}

export function envelopeList<T>(
    envelope: MaeizListEnvelope<T> | undefined | null,
    key: string
): MaeizListResult<T> {
    const items = envelopeField<T[]>(envelope ?? undefined, key, []) ?? [];
    return {
        items: Array.isArray(items) ? items : [],
        currentPage: Number(envelope?.currentPage ?? 1),
        lastPage: Number(envelope?.lastPage ?? 1),
        perPage: Number(envelope?.perPage ?? items.length),
        total: Number(envelope?.total ?? (Array.isArray(items) ? items.length : 0)),
    };
}

export interface NormalizedApiError {
    status: number;
    code: string;
    message: string;
    fieldErrors: Record<string, string[]>;
    requestId?: string;
    isNetworkError: boolean;
    raw?: unknown;
}

const STATUS_CODE_MAP: Record<number, string> = {
    400: "BAD_REQUEST",
    401: "UNAUTHORIZED",
    403: "FORBIDDEN",
    404: "NOT_FOUND",
    409: "CONFLICT",
    422: "VALIDATION_ERROR",
    429: "RATE_LIMIT_EXCEEDED",
    500: "INTERNAL_ERROR",
    502: "BAD_GATEWAY",
    503: "SERVICE_UNAVAILABLE",
    504: "GATEWAY_TIMEOUT",
};

function codeFromStatus(status: number): string {
    return STATUS_CODE_MAP[status] ?? "ERROR";
}

function isStringArrayRecord(value: unknown): value is Record<string, string[]> {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }
    return Object.values(value as Record<string, unknown>).every(
        (entry) => Array.isArray(entry) && entry.every((line) => typeof line === "string")
    );
}

function extractFieldErrors(detail: MaeizErrorDetail | undefined): Record<string, string[]> {
    const details = detail?.details;
    if (!details) {
        return {};
    }
    if (isStringArrayRecord(details.validation)) {
        return details.validation;
    }
    const { validation: _validation, code: _code, ...rest } = details;
    if (isStringArrayRecord(rest)) {
        return rest;
    }
    return {};
}

export function normalizeApiError(error: unknown): NormalizedApiError {
    const axiosLike = error as {
        response?: { status?: number; data?: unknown };
        message?: string;
        code?: string;
    };

    if (!axiosLike || !axiosLike.response) {
        return {
            status: 0,
            code: axiosLike?.code === "ECONNABORTED" ? "TIMEOUT" : "NETWORK",
            message:
                axiosLike?.code === "ECONNABORTED"
                    ? "The request timed out. Please try again."
                    : "Network error — check your connection and try again.",
            fieldErrors: {},
            isNetworkError: true,
            raw: error,
        };
    }

    const status = Number(axiosLike.response.status ?? 0);
    const body = axiosLike.response.data as MaeizErrorEnvelope | undefined;

    if (body && typeof body === "object" && body.error && typeof body.error === "object") {
        return {
            status,
            code: body.error.code ?? codeFromStatus(status),
            message: body.error.message ?? "Request failed.",
            fieldErrors: extractFieldErrors(body.error),
            requestId: body.error.requestId,
            isNetworkError: false,
            raw: body,
        };
    }

    if (body && typeof body === "object" && typeof body.message === "string") {
        return {
            status,
            code: (body.code as string | undefined) ?? codeFromStatus(status),
            message: body.message,
            fieldErrors: {},
            isNetworkError: false,
            raw: body,
        };
    }

    return {
        status,
        code: codeFromStatus(status),
        message:
            (axiosLike.message as string | undefined) ?? "Something went wrong. Please try again.",
        fieldErrors: {},
        isNetworkError: false,
        raw: body ?? error,
    };
}

export function getApiErrorMessage(error: unknown): string {
    return normalizeApiError(error).message;
}

export function firstFieldError(error: unknown): string | undefined {
    const { fieldErrors } = normalizeApiError(error);
    const firstKey = Object.keys(fieldErrors)[0];
    return firstKey ? fieldErrors[firstKey]?.[0] : undefined;
}
